import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import {
  getIdToken,
  getRefreshToken,
  getStoredUser,
  storeSession,
} from '../utils/authStorage';

/**
 * Profile panel: displayName + email.
 * displayName save updates local session; optionally syncs Firebase Auth via
 * Identity Toolkit when REACT_APP_FIREBASE_API_KEY is set (client-safe key).
 */
export default function AccountProfileDialog({ open, onClose, user }) {
  const [displayName, setDisplayName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    if (!open) return;
    setDisplayName(user?.displayName || '');
    setError(null);
    setSuccess(null);
  }, [open, user?.displayName]);

  const handleSave = async () => {
    const nextName = displayName.trim();
    if (!nextName) {
      setError('Display name is required.');
      return;
    }

    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const idToken = getIdToken();
      const refreshToken = getRefreshToken();
      const currentUser = getStoredUser() || user || {};
      const apiKey = process.env.REACT_APP_FIREBASE_API_KEY;

      if (apiKey && idToken) {
        try {
          const res = await fetch(
            `https://identitytoolkit.googleapis.com/v1/accounts:update?key=${apiKey}`,
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                idToken,
                displayName: nextName,
                returnSecureToken: true,
              }),
            }
          );
          const data = await res.json();
          if (!res.ok) {
            throw new Error(data?.error?.message || 'Could not update Firebase profile');
          }
          if (data.idToken) {
            storeSession({
              idToken: data.idToken,
              refreshToken: data.refreshToken || refreshToken,
              user: { ...currentUser, displayName: nextName },
            });
          } else {
            storeSession({
              idToken,
              refreshToken,
              user: { ...currentUser, displayName: nextName },
            });
          }
          setSuccess('Display name updated.');
        } catch (firebaseErr) {
          console.error('❌ Firebase updateProfile failed:', firebaseErr);
          storeSession({
            idToken,
            refreshToken,
            user: { ...currentUser, displayName: nextName },
          });
          setSuccess('Saved for this session. Firebase sync failed — name may reset on next login.');
        }
      } else {
        storeSession({
          idToken,
          refreshToken,
          user: { ...currentUser, displayName: nextName },
        });
        setSuccess(
          apiKey
            ? 'Display name updated for this session.'
            : 'Saved for this session. Add REACT_APP_FIREBASE_API_KEY to sync with Firebase Auth.'
        );
      }
    } catch (err) {
      console.error('❌ Profile save failed:', err);
      setError(err.message || 'Could not save display name.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>Profile</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Your account identity in ZenRentFlow.
        </Typography>
        <TextField
          label="Display name"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          fullWidth
          margin="dense"
          autoFocus
        />
        <TextField
          label="Email"
          value={user?.email || ''}
          fullWidth
          margin="dense"
          InputProps={{ readOnly: true }}
          helperText="Email cannot be changed here."
        />
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mt: 2 }}>
            {success}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : 'Save name'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
